import React from "react";
import Image from "next/image";
import Link from "next/link";
import { InstagramIcon, LinkedinIcon } from "@/assets/svg";
import FacebookIcon from "@/assets/svg/footerSvg/facebook";

const LOGO_ON_LIGHT = "/logos/nivalabs-on-light.png";
const LOGO_ON_DARK = "/logos/nivalabs-on-dark.png";

const COMPANY_LINKS = [
  { href: "/about", label: "About" },
  { href: "/how-we-work", label: "How we work" },
  { href: "/use-cases", label: "Use cases" },
  { href: "/faq", label: "FAQ" },
  { href: "/blogs", label: "Blog" },
  { href: "/contact", label: "Contact" },
];

const SERVICE_LINKS = [
  { href: "/solutions", label: "Solutions" },
  { href: "/ai-agent-development", label: "AI agents" },
  { href: "/custom-llm-application-development", label: "LLM applications" },
  { href: "/ai-workflow-automation", label: "Workflow automation" },
  { href: "/ai-integrations", label: "Integrations" },
  { href: "/mvp-poc-development", label: "MVP & PoC" },
];

const LEGAL_LINKS = [
  { href: "/privacy", label: "Privacy" },
  { href: "/terms", label: "Terms" },
];

function FooterNav({ label, links }) {
  return (
    <nav aria-label={label} className="min-w-[8.5rem]">
      <p className="text-sm font-semibold text-ink">{label}</p>
      <ul className="mt-3 space-y-1.5">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              className="text-sm text-ink-muted no-underline transition-colors hover:text-[#1c8c80]"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

const Footer = () => {
  return (
    <footer className="mt-8 border-t border-line bg-muted py-12 text-ink md:mt-12 md:py-14">
      <div className="container max-w-7xl">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
          <div className="max-w-sm shrink-0">
            <Image
              src={LOGO_ON_LIGHT}
              alt="Nivalabs"
              width={200}
              height={32}
              className="theme-logo-light h-7 w-auto"
              quality={100}
            />
            <Image
              src={LOGO_ON_DARK}
              alt="Nivalabs"
              width={200}
              height={32}
              className="theme-logo-dark h-7 w-auto"
              quality={100}
            />
            <p className="mt-4 text-sm leading-relaxed text-ink-muted">
              Production AI agents, LLM applications, and workflow systems for
              operational teams.
            </p>
            <div className="mt-5 flex gap-3">
              <Link
                href="https://www.instagram.com/nivalabs.ai/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </Link>
              <Link
                href="https://www.linkedin.com/company/nivalabs-ai/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <LinkedinIcon />
              </Link>
              <Link
                href="https://www.facebook.com/people/Nivalabs-AI/61575541688397/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FacebookIcon />
              </Link>
            </div>
          </div>

          <div className="flex flex-wrap gap-x-10 gap-y-8 sm:gap-x-12">
            <FooterNav label="Company" links={COMPANY_LINKS} />
            <FooterNav label="Capabilities" links={SERVICE_LINKS} />
            <FooterNav label="Legal" links={LEGAL_LINKS} />
          </div>
        </div>

        <div className="mt-10 border-t border-line pt-6 text-center text-xs text-ink-soft md:text-left md:text-sm">
          © {new Date().getFullYear()}{" "}
          <span className="text-ink-muted">Nivalabs AI Pvt. Ltd.</span>
          {" · "}A{" "}
          <a
            href="https://pysquad.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1c8c80] no-underline hover:underline"
          >
            PySquad
          </a>{" "}
          company
        </div>
      </div>
    </footer>
  );
};

export default Footer;
